/**
 * Operations & Field Work Time Entry Templates
 * Software/hardware setup, project management, travel, on-site work,
 * routine maintenance, vendor management, capacity planning, and DR.
 */

import type { TimeEntryTemplateData } from "./types";

export const OPERATIONS_TEMPLATES = [
  {
    id: "software-installation",
    name: "Software Installation",
    description: "Installed and configured software applications",
    category: "Software",
    fields: {
      description: "Software installation and configuration - {{software_name}}",
      type: "billable",
      billable_rate: 100,
      tags: ["software", "installation", "configuration", "setup"],
    },
  },
  {
    id: "hardware-setup",
    name: "Hardware Setup",
    description: "Set up and configured hardware devices",
    category: "Hardware",
    fields: {
      description: "Hardware setup and configuration - {{device_type}}",
      type: "billable",
      billable_rate: 100,
      tags: ["hardware", "setup", "configuration", "installation"],
    },
  },
  {
    id: "project-planning",
    name: "Project Planning",
    description: "Planned and scoped IT projects",
    category: "Project Management",
    fields: {
      description: "Project planning and scoping for {{project_name}}",
      type: "billable",
      billable_rate: 125,
      tags: ["planning", "project", "scoping", "management"],
    },
  },
  {
    id: "travel-time",
    name: "Travel Time",
    description: "Travel time to client sites",
    category: "Travel",
    fields: {
      description: "Travel time to {{client_site}}",
      type: "travel",
      billable_rate: 100,
      tags: ["travel", "client-site", "transportation"],
    },
  },
  {
    id: "on-site-visit",
    name: "On-Site Visit",
    description: "On-site work at client location",
    category: "On-Site",
    fields: {
      description: "On-site work at {{client_site}} - {{work_description}}",
      type: "billable",
      billable_rate: 125,
      tags: ["on-site", "client", "visit", "field-work"],
    },
  },
  {
    id: "remote-support",
    name: "Remote Support",
    description: "Provided remote technical support",
    category: "Support",
    fields: {
      description: "Remote support session for {{user_name}} - {{issue_type}}",
      type: "billable",
      billable_rate: 100,
      tags: ["remote", "support", "troubleshooting", "assistance"],
    },
  },
  {
    id: "system-maintenance",
    name: "System Maintenance",
    description: "Performed routine system maintenance",
    category: "Maintenance",
    fields: {
      description: "System maintenance for {{system_name}} - {{maintenance_type}}",
      type: "billable",
      billable_rate: 100,
      tags: ["maintenance", "routine", "systems", "upkeep"],
    },
  },
  {
    id: "vendor-management",
    name: "Vendor Management",
    description: "Managed vendor relationships and contracts",
    category: "Vendor Relations",
    fields: {
      description: "Vendor management activities with {{vendor_name}}",
      type: "billable",
      billable_rate: 125,
      tags: ["vendor", "management", "contracts", "relationships"],
    },
  },
  {
    id: "capacity-planning",
    name: "Capacity Planning",
    description: "Analyzed and planned system capacity",
    category: "Planning",
    fields: {
      description: "Capacity planning analysis for {{system_name}}",
      type: "billable",
      billable_rate: 125,
      tags: ["capacity", "planning", "analysis", "scaling"],
    },
  },
  {
    id: "disaster-recovery",
    name: "Disaster Recovery",
    description: "Worked on disaster recovery planning or testing",
    category: "Disaster Recovery",
    fields: {
      description: "Disaster recovery {{activity_type}} for {{system_name}}",
      type: "billable",
      billable_rate: 150,
      tags: ["disaster-recovery", "planning", "testing", "continuity"],
    },
  },
] as const satisfies readonly TimeEntryTemplateData[];
